import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./MechatronikTypes.module.scss";
import MechatronikTypesAccordion from "./MechatronikTypesAccordion";

interface AccordionSection {
  title: string;
  content: string;
}

interface MechatronikType {
  id: string;
  tabLabel: string;
  title: string;
  subtitle?: string;
  tags: string[];
  images: string[];
  imageAlt: string;
  description: string | string[];
  sections: AccordionSection[];
}

const MechatronikTypes: React.FC = () => {
  const { t } = useTranslation("common");
  const [activeTypeIndex, setActiveTypeIndex] = useState<number>(0);
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number | null>(
    null,
  );
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [activeTagIndex, setActiveTagIndex] = useState<number>(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sectionTitle = t("mechatronikTypes.title");
  const sidebarLabel = t("mechatronikTypes.sidebarLabel");
  const types = t("mechatronikTypes.types", {
    returnObjects: true,
  }) as MechatronikType[];
  const activeType = types[activeTypeIndex];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTypeChange = (index: number) => {
    setActiveTypeIndex(index);
    setOpenAccordionIndex(null);
    setCurrentImageIndex(0);
    setActiveTagIndex(0);
  };

  const handleAccordionToggle = (index: number) => {
    setOpenAccordionIndex((prev) => (prev === index ? null : index));
  };

  if (!activeType) return null;

  const images = activeType.images || [];

  return (
    <div className="container section">
      <div className="sectionContainer bigSection">
        <div className="sectionTitle sideP64">
          <h2>{sectionTitle}</h2>
        </div>
        <div className={`${styles.wrapper} sideP64`}>
          <div className={styles.sidebar}>
            <p className={styles.sidebarLabel}>{sidebarLabel}</p>

            {/* Mobile dropdown */}
            <div className={styles.mobileDropdown} ref={dropdownRef}>
              <button
                className={`${styles.mobileDropdownTrigger} ${isDropdownOpen ? styles.mobileDropdownTriggerOpen : ""}`}
                onClick={() => setIsDropdownOpen((v) => !v)}
              >
                <span>{types[activeTypeIndex]?.tabLabel}</span>
                <svg
                  className={`${styles.dropdownArrow} ${isDropdownOpen ? styles.dropdownArrowOpen : ""}`}
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m6 9 6 6 6-6"
                  />
                </svg>
              </button>
              {isDropdownOpen && (
                <div className={styles.mobileDropdownList}>
                  {types.map((type, index) => (
                    <button
                      key={type.id}
                      className={`${styles.mobileDropdownItem} ${index === activeTypeIndex ? styles.mobileDropdownItemActive : ""}`}
                      onClick={() => {
                        handleTypeChange(index);
                        setIsDropdownOpen(false);
                      }}
                    >
                      <span>{type.tabLabel}</span>
                      {index === activeTypeIndex && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="m5 12 5 5L20 7"
                          />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {types.map((type, index) => (
              <button
                key={type.id}
                className={`${styles.typeButton} ${
                  index === activeTypeIndex ? styles.typeButtonActive : ""
                }`}
                onClick={() => handleTypeChange(index)}
              >
                {type.tabLabel}
              </button>
            ))}
          </div>
          <div className={styles.content}>
            <div className={styles.card}>
              <div className={styles.imageWrapper}>
                <img
                  src={images[currentImageIndex] || images[0]}
                  alt={activeType.imageAlt}
                  className={styles.mainImage}
                />
                {images.length > 1 && (
                  <div className={styles.thumbnails}>
                    {images.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`${activeType.imageAlt} ${index + 1}`}
                        className={`${styles.thumb} ${
                          currentImageIndex === index ? styles.active : ""
                        }`}
                        onClick={() => setCurrentImageIndex(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.descriptionWrapper}>
                <h3 className={styles.typeTitle}>{activeType.title}</h3>
                {activeType.subtitle && (
                  <p className={styles.subtitle}>{activeType.subtitle}</p>
                )}
                {/* segTrack disabled */}
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{
                    __html: Array.isArray(activeType.description)
                      ? (activeType.description[activeTagIndex] ?? activeType.description[0])
                      : activeType.description,
                  }}
                />
                {activeType.sections.map((section, idx) => (
                  <MechatronikTypesAccordion
                    key={`${activeType.id}-${idx}`}
                    title={section.title}
                    content={section.content}
                    isOpen={openAccordionIndex === idx}
                    onToggle={() => handleAccordionToggle(idx)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MechatronikTypes;
